// components/common/MarkdownToolbar.js - Markdown editing toolbar with shortcuts
import React from 'react';
import {
  Bold, Italic, Heading1, Heading2, Heading3,
  List, ListOrdered, Code, Quote, Link2, Image,
  Table, Minus, CheckSquare
} from 'lucide-react';

const ACTIONS = [
  { icon: Bold, label: 'Bold', before: '**', after: '**', placeholder: 'bold text' },
  { icon: Italic, label: 'Italic', before: '_', after: '_', placeholder: 'italic text' },
  { icon: Code, label: 'Inline code', before: '`', after: '`', placeholder: 'code' },
  'sep',
  { icon: Heading1, label: 'Heading 1', before: '# ', after: '', placeholder: 'Heading', line: true },
  { icon: Heading2, label: 'Heading 2', before: '## ', after: '', placeholder: 'Heading', line: true },
  { icon: Heading3, label: 'Heading 3', before: '### ', after: '', placeholder: 'Heading', line: true },
  'sep',
  { icon: List, label: 'Bullet list', before: '- ', after: '', placeholder: 'List item', line: true },
  { icon: ListOrdered, label: 'Numbered list', before: '1. ', after: '', placeholder: 'List item', line: true },
  { icon: CheckSquare, label: 'Task list', before: '- [ ] ', after: '', placeholder: 'Task', line: true },
  'sep',
  { icon: Quote, label: 'Blockquote', before: '> ', after: '', placeholder: 'Quote', line: true },
  { icon: Minus, label: 'Horizontal rule', insert: '\n---\n' },
  { icon: Link2, label: 'Link', before: '[', after: '](url)', placeholder: 'link text' },
  'sep',
  { icon: Code, label: 'Code block', before: '\n```\n', after: '\n```\n', placeholder: 'code here', isBlock: true },
  { icon: Table, label: 'Table', insert: '\n| Column 1 | Column 2 | Column 3 |\n|----------|----------|----------|\n| Cell 1   | Cell 2   | Cell 3   |\n' },
];

export default function MarkdownToolbar({ textareaRef, value, onChange }) {
  const applyAction = (action) => {
    const textarea = textareaRef?.current;
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selected = value.substring(start, end);

    if (action.insert) {
      // Direct insert (tables, hr)
      const newValue = value.substring(0, start) + action.insert + value.substring(end);
      onChange(newValue);
      setTimeout(() => {
        textarea.focus();
        textarea.selectionStart = textarea.selectionEnd = start + action.insert.length;
      }, 0);
      return;
    }

    const before = action.before || '';
    const after = action.after || '';
    const text = selected || action.placeholder || '';

    let newValue;
    if (action.line && !selected) {
      // Line-level action: insert at start of current line
      const lineStart = value.lastIndexOf('\n', start - 1) + 1;
      newValue = value.substring(0, lineStart) + before + text + after + value.substring(start);
      onChange(newValue);
      setTimeout(() => {
        textarea.focus();
        const newPos = lineStart + before.length;
        textarea.selectionStart = newPos;
        textarea.selectionEnd = newPos + text.length;
      }, 0);
    } else {
      newValue = value.substring(0, start) + before + text + after + value.substring(end);
      onChange(newValue);
      setTimeout(() => {
        textarea.focus();
        textarea.selectionStart = start + before.length;
        textarea.selectionEnd = start + before.length + text.length;
      }, 0);
    }
  };

  return (
    <div className="flex items-center gap-0.5 px-3 py-2 bg-surface-muted border-b border-line overflow-x-auto">
      {ACTIONS.map((action, i) => {
        if (action === 'sep') {
          return <div key={i} className="w-px h-5 bg-gray-300 mx-1" />;
        }
        const Icon = action.icon;
        return (
          <button
            key={i}
            onClick={() => applyAction(action)}
            title={action.label}
            className="p-1.5 text-mutedink hover:text-ink hover:bg-surface-subtle rounded transition-colors"
          >
            <Icon size={16} />
          </button>
        );
      })}
    </div>
  );
}
